import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import { saveComment } from '../api/content'; // your API client


export const addComment = createAsyncThunk(
  'comments/addComment',
  async ({ contentId, parentId, text }, { getState, rejectWithValue }) => {
    const state = getState();
    const user = state.auth?.user || null;

    const body = {
      content_id: String(contentId),
      text,
      comment_id: parentId || null,
      // backend table has "user" (varchar). Put the nickname/login there:
      user: user?.nick || user?.login || 'Guest',
      // if your backend/table now supports this, it will be used; otherwise ignored
      user_id: user?.id || null,
    };

    try {
      const saved = await saveComment(body); // should return the created row
      // Normalize for our store; ensure dt is ISO string
      return {
        id: String(saved.id),
        parent_id: saved.comment_id ? String(saved.comment_id) : null,
        user: saved.user || body.user,
        text: (saved.text || '').trim(),
        dt: saved.dt ? String(saved.dt) : new Date().toISOString(),
      };
    } catch (e) {
      return rejectWithValue(e?.message || 'Failed to save comment');
    }
  }
);

const slice = createSlice({
  name: 'comments',
  initialState: { byContent: {} },
  reducers: {
    setCommentsForContent(state, action) {
      const { contentId, comments } = action.payload;
      state.byContent[String(contentId)] = comments.map((c) => ({
        id: String(c.id ?? nanoid()),
        parent_id: c.comment_id ? String(c.comment_id) : null,
        user: c.user || c.author || 'Guest',
        text: String(c.text ?? c.body ?? '').trim(),
        dt: c.dt ? new Date(c.dt).toISOString() : new Date().toISOString(),
      }));
    },
    addOptimistic: {
      reducer(state, action) {
        const { contentId } = action.payload;
        const key = String(contentId);
        state.byContent[key] = [action.payload, ...(state.byContent[key] || [])];
      },
      prepare({ contentId, parentId, text, userDisplay }) {
        return {
          payload: {
            id: `temp_${nanoid()}`,
            contentId: String(contentId),
            parent_id: parentId || null,
            user: userDisplay || 'You',
            text: String(text || '').trim(),
            dt: new Date().toISOString(), // keep Redux serializable
          },
        };
      },
    },
  },
  extraReducers: (b) => {
    b.addCase(addComment.fulfilled, (state, action) => {
      const contentId = String(action.payload._contentId);
      const current = state.byContent[contentId] || [];
      // Drop temps; prepend the real row
      const stable = current.filter((c) => !String(c.id).startsWith('temp_'));
      state.byContent[contentId] = [action.payload, ...stable];
    });
  },
});


export const { setCommentsForContent, addOptimistic } = slice.actions;
export const selectCommentsFor = (s, id) => s.comments.byContent[String(id)] || [];
export default slice.reducer;